import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Form,
    FormField,
    Heading,
    Layer,
    Text,
    TextInput,
    Tip,
    ToggleGroup,
} from 'grommet';
import { Close, Edit } from 'grommet-icons';
import PropTypes from 'prop-types';
import {
    API,
    PipelineService,
    FilterOptionSelect,
    SearchSelect,
} from '@enrich/shared-lib';
import QueryString from 'qs';

export const EditPipelineButton = ({ pipeline }) => {
    const [showModal, setShowModal] = useState(false);

    return (
        <>
            <Tip content="edit pipeline">
                <Button
                    secondary
                    size="medium"
                    label=<span>
                        <Edit size="medium" color="enrich-blue" />
                    </span>
                    onClick={() => setShowModal(true)}
                />
            </Tip>
            {showModal && (
                <EditPipelineModal
                    pipeline={pipeline}
                    setShowModal={setShowModal}
                />
            )}
        </>
    );
};

const EditPipelineModal = ({ pipeline, setShowModal }) => {
    const [companyJobs, setCompanyJobs] = useState([]);
    const [value, setValue] = useState(pipeline.attributes);
    const [buttonBusy, setButtonBusy] = useState();

    const company = pipeline.attributes.company.data;

    useEffect(() => {
        (async () => {
            const loadedCompanyJobs = await loadSelectableCompanyJobs(
                company.id,
                pipeline.id,
            );

            setCompanyJobs(
                loadedCompanyJobs.data.map((job) => ({
                    id: job.id,
                    title: job.attributes.title,
                })),
            );
        })();
    }, []);

    const handleSubmit = async () => {
        try {
            setButtonBusy(true);
            await new PipelineService().update(pipeline.id, value);
            setShowModal(false);
            location.reload();
        } catch (error) {
            console.log(error);
        }

        setButtonBusy(false);
    };

    return (
        <Layer
            onClickOutside={() => setShowModal(false)}
            onEsc={() => setShowModal(false)}
            position="center">
            <Box
                pad="medium"
                round="6px"
                border={{ color: 'enrich-green-light', size: '3px' }}
                gap="30px"
                width={'large'}>
                <Box fill={'horizontal'} direction="row" justify="between">
                    <Box>
                        <Heading level="3" margin={{ vertical: 'none' }}>
                            Edit Pipeline
                        </Heading>
                    </Box>
                    <Box
                        align="end"
                        round={true}
                        onClick={() => {
                            setShowModal(false);
                        }}
                        cursor="pointer">
                        <Close color="enrich-red" />
                    </Box>
                </Box>
                <Box>
                    <Form
                        value={value}
                        onChange={(nextValue) => setValue(nextValue)}
                        onReset={() => setValue({ pipeline })}
                        onSubmit={({ value }) => {
                            handleSubmit(value);
                        }}>
                        <Box gap={'small'}>
                            <Box
                                direction="row-responsive"
                                gap={'medium'}
                                fill="horizontal">
                                <Box basis="large">
                                    <FormField
                                        label="Name"
                                        name="name"
                                        htmlFor="name">
                                        <TextInput
                                            id="name"
                                            name="name"
                                            required
                                        />
                                    </FormField>
                                </Box>
                                <Box basis="medium">
                                    <Tip
                                        content={
                                            value.isReportingActive
                                                ? 'reporting activated'
                                                : 'reporting deactivated'
                                        }>
                                        <FormField
                                            label="Reporting Status"
                                            name="isReportActive">
                                            <ToggleGroup
                                                a11yTitle="Toggle Reporting active status for this school"
                                                margin={{ left: 'small' }}
                                                value={
                                                    // eslint-disable-next-line max-len
                                                    value.isReportingActive?.toString() ||
                                                    'false'
                                                }
                                                onToggle={({
                                                    value: toggleValue,
                                                }) =>
                                                    setValue({
                                                        ...value,
                                                        isReportingActive:
                                                            toggleValue ===
                                                            'true',
                                                    })
                                                }
                                                options={[
                                                    {
                                                        label: (
                                                            <Text color="enrich-red">
                                                                OFF
                                                            </Text>
                                                        ),
                                                        value: 'false',
                                                    },
                                                    {
                                                        label: (
                                                            <Text color="enrich-green">
                                                                ON
                                                            </Text>
                                                        ),
                                                        value: 'true',
                                                    },
                                                ]}
                                            />
                                        </FormField>
                                    </Tip>
                                </Box>
                            </Box>

                            <Box>
                                <FormField
                                    label="Company"
                                    name="company"
                                    htmlFor="company"
                                    required>
                                    <SearchSelect
                                        name="company"
                                        labelKey="name"
                                        valueKey="id"
                                        defaultValue={{
                                            id: company.id,
                                            name: company.attributes.name,
                                        }}
                                        options={[
                                            {
                                                id: company.id,
                                                name: company.attributes.name,
                                            },
                                        ]}
                                        disabled={true}
                                    />
                                </FormField>
                            </Box>

                            <Box>
                                <FormField
                                    name="jobs"
                                    htmlFor="jobs"
                                    label="Jobs">
                                    <FilterOptionSelect
                                        labelKey="title"
                                        placeholder="select jobs to add to pipeline"
                                        options={companyJobs}
                                        updateFormData={(selectedJobs) => {
                                            setValue({
                                                ...value,
                                                jobs: selectedJobs.map(
                                                    (x) => x.id,
                                                ),
                                            });
                                        }}
                                        valueKey="id"
                                        // eslint-disable-next-line max-len
                                        defaultValue={pipeline.attributes.jobs.data.map(
                                            (x) => Number(x.id),
                                        )}
                                    />
                                </FormField>
                            </Box>
                            <Box>
                                <Button
                                    primary
                                    type="submit"
                                    size="large"
                                    fill="horizontal"
                                    busy={buttonBusy}
                                    label="Submit"
                                    alignSelf="center"
                                />
                            </Box>
                        </Box>
                    </Form>
                </Box>
            </Box>
        </Layer>
    );
};

const loadSelectableCompanyJobs = async (companyId, pipelineId) => {
    const query = QueryString.stringify(
        {
            filters: {
                company: { id: { $eq: companyId } },
                pipeline: {
                    $or: [{ id: { $null: true } }, { id: pipelineId }],
                },
            },
            fields: ['id', 'title'],
            sort: ['title:asc'],
            pagination: { limit: 10000 },
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/jobs?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
    }

    return [];
};

EditPipelineButton.propTypes = {
    pipeline: PropTypes.object,
};

EditPipelineModal.propTypes = {
    pipeline: PropTypes.object,
    setShowModal: PropTypes.func,
};
